// Modules
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import Korg from 'reed-korg'


// Components
import BaseComponent from '@blocks/_BaseComponent'

const MINIMAL_THRESHOLD = 150




/* MENU
----------------------------------------------------------------------------------------------------*/

export default class Menu extends BaseComponent {

    constructor($el) {
        super('Menu', $el, '.global')


        this.parser = new DOMParser()
        this.isOpen = false
        this.isMinimal = this.$utils.body.hasClass('minimal-ui')
        this.shouldAlwaysBeMinimal = this.$utils.body.hasClass('base-minimal-ui')
        this.highlightedItem = null
        this.hoveredItem = null
        this.korg = new Korg(this.$refs.scrollArea[0])

        this.getItems()
        this.moveHighlighter(false, 0)
        this.onScroll()

        this.initButtonScrollTrigger()
        this.initTimeline()
        this.on()
    }


    bindHandlers() {
        this.mouseDownHandler      = (e) => { this.onMouseDown(e) }
        this.mouseUpHandler        = (e) => { this.onMouseUp(e) }
        this.clickHandler          = (e) => { this.onButtonClick(e) }
        this.pageWillChangeHandler = () => { this.onPageWillChange() }
        this.pageChangeEndHandler  = (e) => { this.onPageChangeEnd(e.detail) }
        this.resizeHandler         = () => { this.onResize() }
        this.scrollHandler         = () => { this.onScroll() }
    }


    bindEvents() {
        const { $win, $doc } = this.$utils
        const { $menuBtn, $item } = this.$refs


        this.addListeners(
            // Nav events
            [$doc, 'pageWillChange', this.pageWillChangeHandler],
            [$doc, 'pageChangeEnd', this.pageChangeEndHandler],

            // Layout events
            [$win, 'scroll', this.scrollHandler],
            [$doc, 'layoutChange', this.resizeHandler],

            // User input
            [$menuBtn, 'click', this.clickHandler],
            [$item, 'mousedown', this.mouseDownHandler],
            [$item, 'mouseup', this.mouseUpHandler],
        )
    }




    /* Init
    --------------------------------------------------------- */

    initButtonScrollTrigger() {
        this.button = document.querySelector('a[href="#steps"]')
        if (this.button) {
            gsap.set(this.button, { opacity: 0, pointerEvents: 'none', transform: 'translateY(200%)', duration: 0.3 })

            this.buttonTrigger = ScrollTrigger.create({
                trigger: '#steps',
                start: 'top top', 
                onLeave: () => gsap.to(this.button, { opacity: 1, pointerEvents: 'auto', transform: 'translateY(0)', duration: 0.3 }),
                onEnterBack: () => gsap.to(this.button, { opacity: 0, pointerEvents: 'none', transform: 'translateY(200%)', duration: 0.3 }),
            })

            this.relatedTrigger = ScrollTrigger.create({
                trigger: 'aside.relatedGuides',
                start: 'top bottom',
                onEnter: () => gsap.to(this.button, { opacity: 0, pointerEvents: 'none', transform: 'translateY(200%)', duration: 0.3 }),
                onLeaveBack: () => gsap.to(this.button, { opacity: 1, pointerEvents: 'auto', transform: 'translateY(0)', duration: 0.3 }),
            })
        }
    }

    initTimeline() {
        if (this.tl) {
            this.tl.scrollTrigger.kill(true)
            this.tl.kill(true)
            this.tl.clear()
            this.tl = null
            gsap.set(this.$el, { clearProps: 'all' })
        }

        if (this.shouldAlwaysBeMinimal) return

        this.tl = gsap.timeline({
            scrollTrigger: {
                trigger: '.view:not(.is-going)',
                start: '0',
                end: MINIMAL_THRESHOLD,
                scrub: true,
            },
        })

        this.tl.fromTo(this.$el, { '--navOffset': 0 }, { '--navOffset': 1, ease: 'none' })
    }




    /* Handlers
    --------------------------------------------------------- */

    onPageWillChange() {
        if (this.buttonTrigger) {
            this.buttonTrigger.kill(true)
        }
        if (this.relatedTrigger) {
            this.relatedTrigger.kill(true)
        }
    }


    onPageChangeEnd({ next }) {
        const $nextHTML = this.parser.parseFromString(next.html, 'text/html')
        const $nextBody = $nextHTML.querySelector('body')

        this.shouldAlwaysBeMinimal = $nextBody.classList.contains('base-minimal-ui')

        this.getHLitem()
        this.toggleMinimalUI(this.shouldAlwaysBeMinimal, true)
        this.moveHighlighter()
        this.initButtonScrollTrigger()
        this.initTimeline()

        this.$utils.$body.toggleClass('base-minimal-ui', this.shouldAlwaysBeMinimal)
        this.toggleMenu(false)
    }


    onResize() {
        if (!this.isOpen || APP.Layout.isMobile) return

        this.toggleMenu(false)
    }


    onScroll() {
        if (APP.Router.isTransitioning) return
        const shouldBeMinimal = this.shouldAlwaysBeMinimal || (checkIfMinimal() && !this.hoveredItem)

        this.toggleMinimalUI(shouldBeMinimal)
    }


    onButtonClick(e) {
        e.preventDefault()

        if (APP.Layout.isMobile) {
            this.toggleMenu()
        } else {
            this.toggleMinimalUI(false)
        }
    }


    onItemMouseEnter(item) {
        clearTimeout(this.hoverTimer)

        const fromScratch = this.hoveredItem === null && !this.highlightedItem

        this.highlightedItem = item
        this.hoveredItem = item
        this.moveHighlighter(fromScratch)
    }


    onItemMouseLeave() {
        this.hoverTimer = setTimeout(() => {
            this.highlightedItem = this.items.find(i => i.isCurrent)
            this.hoveredItem = null
            this.moveHighlighter()
        }, 60)
    }


    onMouseDown() {
        if (this.hoveredItem) {
            this.isMouseDown = true
            gsap.to(this.$refs.highlighter, { duration: 0.2, alpha: 1, scale: 0.88, ease: 'power3.out' })
        }
    }


    onMouseUp() {
        gsap.to(this.$refs.highlighter, {
            duration: 0.3,
            alpha: 1,
            scale: 1,
            ease: 'power3.out',
            onComplete: () => {
                this.isMouseDown = false
            },
        })
    }




    /* Actions
    --------------------------------------------------------- */

    toggleMenu(shouldOpen = !this.isOpen) {
        if (shouldOpen === this.isOpen) return
        const { $doc, $body } = this.$utils

        this.isOpen = shouldOpen
        this.korg.toggle(this.isOpen)

        $body.toggleClass('menu-open', this.isOpen)
        $doc.trigger('menuToggled', { isOpen : this.isOpen })
        this.$refs.menuBtn.attr('aria-expanded', shouldOpen)
    }


    toggleMinimalUI(shouldBeMinimal = !this.isMinimal, skipHighlight) {
        if (shouldBeMinimal === this.isMinimal) return

        // console.log('toggling', shouldBeMinimal)

        this.isMinimal = shouldBeMinimal
        this.$utils.body.toggleClass('minimal-ui', shouldBeMinimal)
        this.$refs.menuBtn.attr('aria-expanded', !shouldBeMinimal)

        if (!skipHighlight) {
            this.moveHighlighter()
        }
    }


    moveHighlighter(fromScratch, dur = 0.4) {
        if (this.isMouseDown) return

        const shouldAppear = this.highlightedItem && !this.isMinimal
        const width = APP.Layout.isDesktop ? 116 : 110

        const duration = shouldAppear ? dur : 0.6
        const x = shouldAppear ? this.highlightedItem.index * width : null
        const scale = shouldAppear ? 1 : 0
        const alpha = shouldAppear ? 1 : 0


        if (fromScratch) {
            gsap.set(this.$refs.highlighter, { x })
        }

        if (shouldAppear) {
            gsap.to(this.$refs.highlighter, { duration, alpha, x, scale, ease: 'power3.out' })
        } else {
            gsap.to(this.$refs.highlighter, { duration, alpha, scale, ease: 'power3.out' })
        }
    }




    /* Values
    --------------------------------------------------------- */

    getItems() {
        this.items = this.$refs.item.map(($el, index) => ({
            $el,
            index,
            isCurrent: false,
            isHovered: false,
        }))

        this.getHLitem()

        this.items.forEach((item) => {
            this.bindListener(item.$el, 'mouseenter', () => { this.onItemMouseEnter(item) })
            this.bindListener(item.$el, 'mouseleave', () => { this.onItemMouseLeave(item) })
        })
    }


    getHLitem() {
        this.items.forEach((item) => {
            item.isCurrent = item.$el.classList.contains('is-current')
        })

        this.highlightedItem = this.items.find(i => i.isCurrent)
    }

}




/* CONSTANTS AND HELPERS
---------------------------------------------------------------------------------------------------- */

const checkIfMinimal = () => APP.Layout.scrollTop >= MINIMAL_THRESHOLD



